import { useEffect, useState } from 'react';
import type { CuponDelCatalogo, PrecioDelCatalogo, ProductoDelCatalogo, ReglaDeComision } from '../../api/ops/backoffice';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { errorDeCampo, faltanDosPasos } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { textoDeError } from '../negocios/logica';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { ERROR_MOTIVO, motivoValido } from '../negocios/logica';
import {
    CUPON_VACIO,
    ERROR_CUPO_PRUEBA,
    ERROR_FIJO,
    ERROR_PORCENTAJE,
    ERROR_PRECIO,
    apartadoDe,
    centimosDe,
    centimosFijos,
    cuponCaducado,
    cupoDePrueba,
    cupoEscrito,
    erroresCupon,
    fechaLarga,
    fechasDesde,
    fechasHasta,
    gruposDelCatalogo,
    hayErrores,
    lineaCupon,
    nuevaComision,
    nuevoCupon,
    nuevoPrecio,
    precioProgramado,
    productosParaCupon,
    puntosBasicosDe,
    textoProgramado,
    textoVentanaComision,
    textoVentanaCupoPrueba,
    textoVentanaPrecio,
    type FormularioCupon,
} from './logica';
import './catalogo.css';

/**
 * **Catálogo y precios** del backoffice (1.21, solo el Superadmin, `plataforma.catalogo.gestionar`). Dos apartados
 * (`?apartado=cupones`): **Precios**, con la base, los bloques, los paquetes de WhatsApp, la comisión de plataforma y la
 * pausa (`listCatalogProducts`, `listPlatformFeeRules`) y «Cambiar precio», que programa un precio nuevo desde el día 1
 * de un mes, solo para contratos nuevos (`createCatalogPrice`, G185); y **Cupones** (`listCatalogCoupons`), con «Crear
 * cupón» en un panel lateral (`createCatalogCoupon`): la aplicación lo crea en Stripe y no se edita (G93).
 *
 * El precio ya programado sale de `next_price` (G1134). La comisión de plataforma tiene su propia ventana con importe
 * fijo, porcentaje y motivo (`createPlatformFeeRule`, G1136).
 */
interface Datos {
    productos: ProductoDelCatalogo[];
    reglas: ReglaDeComision[];
    cupones: CuponDelCatalogo[];
}

export function Catalogo(p: PropsSeccion) {
    const { ops, superadmin, movil, busqueda, cambiarConsulta, onSesionCaducada, onFaltanDosPasos } = p;
    const apartado = apartadoDe(busqueda);
    const [vuelta, setVuelta] = useState(0);
    const [estado, setEstado] = useState<{ vuelta: number; datos: Datos | null; error: unknown }>({ vuelta: -1, datos: null, error: null });
    const [programados, setProgramados] = useState<Readonly<Record<string, PrecioDelCatalogo>>>({});
    const [precio, setPrecio] = useState<ProductoDelCatalogo | null>(null);
    const [cupoPrueba, setCupoPrueba] = useState<ProductoDelCatalogo | null>(null);
    // Lo guardado en «Cupo en la prueba» (la respuesta de updateCatalogProduct), hasta volver a leer la lista.
    const [cambiados, setCambiados] = useState<Readonly<Record<string, ProductoDelCatalogo>>>({});
    const [comision, setComision] = useState<{ nombre: string; regla: ReglaDeComision } | null>(null);
    const [cuponAbierto, setCuponAbierto] = useState(false);
    // Cada vez que se abre «Nuevo cupón», un formulario vacío (otra `key`).
    const [aperturas, setAperturas] = useState(0);

    useEffect(() => {
        let vivo = true;
        Promise.all([ops.listCatalogProducts(), ops.listPlatformFeeRules(), ops.listCatalogCoupons()])
            .then(([productos, reglas, cupones]) => {
                if (vivo) {
                    setEstado({ vuelta, datos: { productos: productos.data, reglas: reglas.data, cupones: cupones.data }, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onSesionCaducada();
                    return;
                }
                if (faltanDosPasos(error)) {
                    onFaltanDosPasos();
                    return;
                }
                setEstado({ vuelta, datos: null, error });
            });
        return () => {
            vivo = false;
        };
    }, [ops, vuelta, onSesionCaducada, onFaltanDosPasos]);

    const datos = estado.vuelta === vuelta ? estado.datos : null;
    const error = estado.vuelta === vuelta ? estado.error : null;
    const ahora = new Date();
    const nombres = Object.fromEntries((datos?.productos ?? []).map((x) => [x.code, x.name]));

    if (error !== null && estadoDeError(error).tipo === 'sin-permiso') {
        return <SinPermiso seccion="Catálogo y precios" superadmin={superadmin} movil={movil} onIr={null} />;
    }
    return (
        <div className="rv-bo-catalogo">
            <div className="rv-bo-catalogo__apartados" style={{ maxWidth: movil ? 'none' : 420 }}>
                <Segmentos
                    items={[
                        { etiqueta: 'Precios', icono: 'tags' },
                        { etiqueta: 'Cupones', icono: 'percent' },
                    ]}
                    valor={apartado === 'cupones' ? 1 : 0}
                    movil={movil}
                    etiqueta="Apartado del catálogo"
                    onCambio={(i) => {
                        cambiarConsulta({ apartado: i === 1 ? 'cupones' : null }, true);
                    }}
                />
            </div>
            {error !== null ? (
                <EstadoError seccion="Catálogo y precios" codigoSoporte={estadoDeError(error).codigoSoporte} movil={movil} onReintentar={() => {
                    setVuelta((v) => v + 1);
                }} />
            ) : datos === null ? (
                <EstadoCargando seccion="Catálogo y precios" />
            ) : apartado === 'precios' ? (
                gruposDelCatalogo(datos.productos, datos.reglas, ahora).map((g) => (
                    <section key={g.titulo} className="rv-bo-catalogo__grupo" aria-label={g.titulo}>
                        <div className="rv-bo-catalogo__cab">
                            <strong>{g.titulo}</strong>
                            <span>{g.nota}</span>
                        </div>
                        <div className="rv-bo-catalogo__tarjeta">
                            {g.filas.map((f, i) => {
                                const siguiente = f.producto === null ? undefined : (programados[f.producto.id] ?? precioProgramado(f.producto, ahora));
                                const producto = f.producto === null ? null : (cambiados[f.producto.id] ?? f.producto);
                                const prueba = producto === null ? null : cupoDePrueba(producto);
                                return (
                                    <div key={f.clave} className="rv-bo-catalogo__fila" style={{ animationDelay: `${String(i * 30)}ms` }}>
                                        <span className="rv-bo-catalogo__textos">
                                            <strong>{f.nombre}</strong>
                                            <span>{f.linea}</span>
                                            {prueba !== null && (
                                                <span className="rv-bo-catalogo__prueba">
                                                    Cupo en la prueba: <b>{prueba.texto}</b>
                                                </span>
                                            )}
                                        </span>
                                        {siguiente !== undefined && siguiente !== null && <Chip estado="pending">{textoProgramado(siguiente)}</Chip>}
                                        {f.programada !== null && <Chip estado="pending">{f.programada}</Chip>}
                                        <span className="rv-bo-catalogo__precio">{f.precio}</span>
                                        {f.producto !== null && (
                                            <button
                                                type="button"
                                                className="rv-bo-catalogo__boton"
                                                aria-label={`Cambiar el precio de ${f.nombre}`}
                                                onClick={() => {
                                                    setPrecio(f.producto);
                                                }}
                                            >
                                                <Icono nombre="pencil" />
                                                <span>Cambiar precio</span>
                                            </button>
                                        )}
                                        {producto !== null && prueba !== null && (
                                            <button
                                                type="button"
                                                className="rv-bo-catalogo__boton"
                                                aria-label={`Cambiar el cupo en la prueba de ${f.nombre}`}
                                                onClick={() => {
                                                    setCupoPrueba(producto);
                                                }}
                                            >
                                                <Icono nombre="gauge" />
                                                <span>Cupo en la prueba</span>
                                            </button>
                                        )}
                                        {f.regla !== null && (
                                            <button
                                                type="button"
                                                className="rv-bo-catalogo__boton"
                                                aria-label={`Cambiar la comisión de ${f.nombre}`}
                                                onClick={() => {
                                                    if (f.regla !== null) {
                                                        setComision({ nombre: f.nombre, regla: f.regla });
                                                    }
                                                }}
                                            >
                                                <Icono nombre="pencil" />
                                                <span>Cambiar precio</span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ))
            ) : (
                <section className="rv-bo-catalogo__grupo" aria-label="Cupones">
                    <div className={`rv-bo-catalogo__cab rv-bo-catalogo__cab--cupones${movil ? ' rv-bo-catalogo__cab--movil' : ''}`}>
                        <span className="rv-bo-catalogo__nota">
                            <strong>Cupones</strong>
                            <span>Se crean aquí y la aplicación los crea en Stripe. No se editan.</span>
                        </span>
                        <button
                            id="bo-cta"
                            type="button"
                            className="rv-bo-catalogo__crear"
                            onClick={() => {
                                setAperturas((n) => n + 1);
                                setCuponAbierto(true);
                            }}
                        >
                            <Icono nombre="plus" />
                            <span>Crear cupón</span>
                        </button>
                    </div>
                    <div className="rv-bo-catalogo__tarjeta">
                        {datos.cupones.map((c, i) => (
                            <div key={c.id} className="rv-bo-catalogo__fila" style={{ animationDelay: `${String(i * 40)}ms` }}>
                                <span className="rv-bo-catalogo__baldosa">
                                    <Icono nombre="percent" />
                                </span>
                                <span className="rv-bo-catalogo__textos">
                                    <strong>
                                        {c.name} <em>· {c.code}</em>
                                    </strong>
                                    <span>{lineaCupon(c, nombres, ahora)}</span>
                                </span>
                                <span className={`rv-bo-catalogo__pct${cuponCaducado(c, ahora) ? ' rv-bo-catalogo__pct--caducado' : ''}`}>{`−${String(c.percent_off)} %`}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}
            {precio !== null && (
                <VentanaPrecio
                    {...p}
                    producto={precio}
                    onCerrar={() => {
                        setPrecio(null);
                    }}
                    onHecho={(nuevo) => {
                        setProgramados((x) => ({ ...x, [precio.id]: nuevo }));
                        setPrecio(null);
                    }}
                />
            )}
            {cupoPrueba !== null && (
                <VentanaCupoPrueba
                    {...p}
                    producto={cupoPrueba}
                    onCerrar={() => {
                        setCupoPrueba(null);
                    }}
                    onHecho={(nuevo) => {
                        setCambiados((x) => ({ ...x, [nuevo.id]: nuevo }));
                        setCupoPrueba(null);
                    }}
                />
            )}
            {comision !== null && (
                <VentanaComision
                    {...p}
                    nombre={comision.nombre}
                    regla={comision.regla}
                    onCerrar={() => {
                        setComision(null);
                    }}
                    onHecho={() => {
                        setComision(null);
                        setVuelta((v) => v + 1);
                    }}
                />
            )}
            {datos !== null && (
                <PanelCupon
                    key={aperturas}
                    {...p}
                    abierto={cuponAbierto}
                    productos={productosParaCupon(datos.productos)}
                    cupones={datos.cupones}
                    onCerrar={() => {
                        setCuponAbierto(false);
                    }}
                    onHecho={() => {
                        setCuponAbierto(false);
                        setVuelta((v) => v + 1);
                    }}
                />
            )}
        </div>
    );
}

/** «Cambiar el precio de …» (prototipo `v.price`): precio sin IVA y «Desde» el día 1 de uno de los tres meses siguientes. */
function VentanaPrecio({ ops, movil, producto, onCerrar, onHecho }: PropsSeccion & { producto: ProductoDelCatalogo; onCerrar: () => void; onHecho: (p: PrecioDelCatalogo) => void }) {
    const avisar = useAvisoBreve();
    const fechas = fechasDesde(new Date());
    const [valor, setValor] = useState('');
    const [desde, setDesde] = useState(String(fechas.length - 1));
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const centimos = centimosDe(valor);
    const error = errorApi ?? (intentado && centimos === null ? ERROR_PRECIO : null);
    const textos = textoVentanaPrecio(producto);

    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        const fecha = fechas[Number(desde)];
        if (centimos === null || fecha === undefined) {
            return;
        }
        setOcupado(true);
        ops.createCatalogPrice(producto.id, nuevoPrecio(producto, centimos, fecha))
            .then(onHecho)
            .catch((e: unknown) => {
                setOcupado(false);
                const delCampo = errorDeCampo(e, 'unit') ?? errorDeCampo(e, 'unit.amount');
                if (delCampo !== null) {
                    setErrorApi(delCampo);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };

    return (
        <Ventana abierta titulo={textos.titulo} texto={textos.texto} accion="Programar el precio" accionIcono="calendar-plus" ocupado={ocupado} movil={movil} onAccion={guardar} onCerrar={onCerrar}>
            <Campo
                etiqueta="Precio nuevo, sin IVA"
                tipo="number"
                valor={valor}
                prefijo="€ "
                error={error}
                onChange={(v) => {
                    setValor(v);
                    setErrorApi(null);
                }}
                onEnter={guardar}
            />
            <Desplegable etiqueta="Desde" valor={desde} opciones={fechas.map((f, i) => ({ valor: String(i), texto: fechaLarga(f) }))} onChange={setDesde} ayuda="Solo para contratos nuevos." />
        </Ventana>
    );
}

/**
 * «Cupo en la prueba» de un producto (G998, G1036; prototipo `v.tquota`): la cifra durante la prueba, o vacío para dejarlo
 * sin cifra (se cuenta sin cortar). `updateCatalogProduct` con `quota_trial_included` y el `etag` del elemento de la lista
 * (`CatalogProduct.etag`, G835, G1188) en `If-Match`; sin él, la ventana no puede guardar.
 */
function VentanaCupoPrueba({ ops, movil, producto, onCerrar, onHecho }: PropsSeccion & { producto: ProductoDelCatalogo; onCerrar: () => void; onHecho: (p: ProductoDelCatalogo) => void }) {
    const avisar = useAvisoBreve();
    const prueba = cupoDePrueba(producto);
    const [valor, setValor] = useState(prueba === null || prueba.cifra === null ? '' : String(prueba.cifra));
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorApi, setErrorApi] = useState<string | null>(null);
    const cifra = cupoEscrito(valor);
    const error = errorApi ?? (intentado && cifra === undefined ? ERROR_CUPO_PRUEBA : null);
    const textos = textoVentanaCupoPrueba(producto, prueba?.unidad ?? 'unidades');
    const etag = producto.etag;

    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (cifra === undefined) {
            return;
        }
        if (etag === undefined) {
            avisar({ tipo: 'error', texto: 'No se ha podido guardar: vuelve a cargar el catálogo y repítelo.' });
            return;
        }
        setOcupado(true);
        ops.updateCatalogProduct(producto.id, { quota_trial_included: cifra }, etag)
            .then(onHecho)
            .catch((e: unknown) => {
                setOcupado(false);
                const delCampo = errorDeCampo(e, 'quota_trial_included');
                if (delCampo !== null) {
                    setErrorApi(delCampo);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };

    return (
        <Ventana abierta titulo={textos.titulo} texto={textos.texto} accion="Guardar el cupo" accionIcono="check" ocupado={ocupado} movil={movil} onAccion={guardar} onCerrar={onCerrar}>
            <Campo
                etiqueta={textos.etiqueta}
                tipo="number"
                valor={valor}
                error={error}
                ayuda={textos.ayuda}
                onChange={(v) => {
                    setValor(v);
                    setErrorApi(null);
                }}
                onEnter={guardar}
            />
        </Ventana>
    );
}

/**
 * «Cambiar precio» de la **comisión de plataforma** (`createPlatformFeeRule`, G1136): importe fijo, porcentaje y motivo,
 * desde el día 1 de uno de los tres meses siguientes (la fecha no puede ser pasada). La regla nueva cierra la actual.
 */
// Provisional hasta C.63: RV Ventana con los campos de siempre, hasta que diseño la dibuje.
function VentanaComision({ ops, movil, nombre, regla, onCerrar, onHecho }: PropsSeccion & { nombre: string; regla: ReglaDeComision; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const fechas = fechasDesde(new Date());
    const [fijo, setFijo] = useState('');
    const [porcentaje, setPorcentaje] = useState('');
    const [motivo, setMotivo] = useState('');
    const [desde, setDesde] = useState('0');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [deApi, setDeApi] = useState<Partial<Record<'fijo' | 'porcentaje' | 'motivo' | 'desde', string>>>({});
    const centimos = centimosFijos(fijo);
    const puntos = puntosBasicosDe(porcentaje);
    const errores = {
        fijo: deApi.fijo ?? (intentado && centimos === null ? ERROR_FIJO : null),
        porcentaje: deApi.porcentaje ?? (intentado && puntos === null ? ERROR_PORCENTAJE : null),
        motivo: deApi.motivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null),
        desde: deApi.desde ?? null,
    };
    const textos = textoVentanaComision(nombre, regla);
    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        const fecha = fechas[Number(desde)];
        if (centimos === null || puntos === null || !motivoValido(motivo) || fecha === undefined) {
            return;
        }
        setOcupado(true);
        ops.createPlatformFeeRule(nuevaComision(regla, centimos, puntos, fecha, motivo))
            .then(() => {
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const api = { fijo: errorDeCampo(e, 'fixed') ?? errorDeCampo(e, 'fixed.amount'), porcentaje: errorDeCampo(e, 'percent_bp'), motivo: errorDeCampo(e, 'reason'), desde: errorDeCampo(e, 'valid_from') };
                if (Object.values(api).some((x) => x !== null)) {
                    setDeApi(Object.fromEntries(Object.entries(api).filter(([, x]) => x !== null)));
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };
    const limpiar = (f: (v: string) => void) => (v: string) => {
        f(v);
        setDeApi({});
    };
    return (
        <Ventana abierta titulo={textos.titulo} texto={textos.texto} accion="Programar la comisión" accionIcono="calendar-plus" ocupado={ocupado} movil={movil} onAccion={guardar} onCerrar={onCerrar}>
            <Campo etiqueta="Importe fijo" tipo="number" valor={fijo} prefijo="€ " error={errores.fijo} ayuda="Por cada entrada. Vacío: sin importe fijo." onChange={limpiar(setFijo)} onEnter={guardar} />
            <Campo etiqueta="Porcentaje" tipo="number" valor={porcentaje} prefijo="% " error={errores.porcentaje} ayuda="Del precio de la entrada. Vacío: sin porcentaje." onChange={limpiar(setPorcentaje)} onEnter={guardar} />
            <Desplegable etiqueta="Desde" valor={desde} opciones={fechas.map((f, i) => ({ valor: String(i), texto: fechaLarga(f) }))} onChange={limpiar(setDesde)} error={errores.desde} />
            <Campo etiqueta="Motivo" valor={motivo} error={errores.motivo} ayuda="Obligatorio. Queda en Auditoría." max={500} onChange={limpiar(setMotivo)} onEnter={guardar} />
        </Ventana>
    );
}

/** «Nuevo cupón» (prototipo `pk === 'cupon'`, RV Panel formulario): se crea también en Stripe y no se edita. */
function PanelCupon({
    ops,
    movil,
    abierto,
    productos,
    cupones,
    onCerrar,
    onHecho,
}: PropsSeccion & { abierto: boolean; productos: { codigo: string; nombre: string }[]; cupones: readonly CuponDelCatalogo[]; onCerrar: () => void; onHecho: () => void }) {
    const avisar = useAvisoBreve();
    const [f, setF] = useState<FormularioCupon>(CUPON_VACIO);
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [deApi, setDeApi] = useState<Partial<Record<'codigo' | 'nombre' | 'porcentaje', string>>>({});
    const errores = erroresCupon(f, cupones);

    const cambiar = (campo: keyof FormularioCupon) => (v: string) => {
        setF((x) => ({ ...x, [campo]: v }));
        setDeApi({});
    };

    const guardar = () => {
        if (ocupado) {
            return;
        }
        setIntentado(true);
        if (hayErrores(errores)) {
            return;
        }
        setOcupado(true);
        ops.createCatalogCoupon(nuevoCupon(f))
            .then(() => {
                setOcupado(false);
                onHecho();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                const api = { codigo: errorDeCampo(e, 'code'), nombre: errorDeCampo(e, 'name'), porcentaje: errorDeCampo(e, 'percent_off') };
                if (Object.values(api).some((x) => x !== null)) {
                    setDeApi(Object.fromEntries(Object.entries(api).filter(([, x]) => x !== null)));
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e) });
            });
    };

    const ver = (campo: 'codigo' | 'nombre' | 'porcentaje') => deApi[campo] ?? (intentado ? errores[campo] : null);
    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            tamano="formulario"
            eyebrow="Catálogo"
            titulo="Nuevo cupón"
            subtitulo="Se crea también en Stripe."
            icono="percent"
            accion="Crear cupón"
            accionIcono="check"
            ocupado={ocupado}
            onAccion={guardar}
        >
            <div className="rv-bo-cupon__campos">
                <Campo etiqueta="Código" valor={f.codigo} onChange={cambiar('codigo')} mayusculas="none" ayuda="Minúsculas, números y _." error={ver('codigo')} max={40} />
                <Campo etiqueta="Nombre" valor={f.nombre} onChange={cambiar('nombre')} error={ver('nombre')} />
                <Campo etiqueta="Descuento en %" tipo="number" modo="numeric" valor={f.porcentaje} onChange={cambiar('porcentaje')} error={ver('porcentaje')} max={3} />
                <Campo etiqueta="Meses que dura" tipo="number" modo="numeric" valor={f.meses} onChange={(v) => {
 cambiar('meses')(v.replace(/\D/g, ''));
}} ayuda="Vacío: sin límite." max={3} />
                <Desplegable etiqueta="Se puede aplicar hasta" valor={f.hasta} opciones={fechasHasta(new Date())} onChange={cambiar('hasta')} />
            </div>
            <div className="rv-bo-cupon__productos">
                <div className="rv-bo-cupon__cab">Se aplica a</div>
                {productos.map((x) => (
                    <Interruptor
                        key={x.codigo}
                        etiqueta={x.nombre}
                        valor={f.productos.includes(x.codigo)}
                        onChange={(on) => {
                            setF((y) => ({ ...y, productos: on ? [...y.productos.filter((c) => c !== x.codigo), x.codigo] : y.productos.filter((c) => c !== x.codigo) }));
                        }}
                    />
                ))}
                {intentado && errores.productos !== null && (
                    <span role="alert" className="rv-bo-cupon__error">
                        <Icono nombre="circle-alert" />
                        {errores.productos}
                    </span>
                )}
            </div>
            <div className="rv-bo-cupon__nota">
                <Icono nombre="info" />
                Un cupón no se edita: para cambiar el porcentaje, los productos o la duración, crea otro.
            </div>
        </PanelLateral>
    );
}
